import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs';
import { getMetaValue } from "../plugins/src/metadata"

// Controller for drag-to-reorder slides.
//
// On the legacy editor a single container holds all slides for a row;
// on the themed inner-body chrome the controller is mounted on
// `.sh-journal-body-inner` AND on each `<ul class="sh-journal-staff">`
// nested inside it. The `group: "sections"` setting lets the user
// drag a card out of a staff grid into the body flow (or vice versa);
// the existing legacy mount carries the same group name but never has
// a sibling container, so behavior there is unchanged.
//
// `event.newIndex` is per-container (it's the SortableJS index inside
// the immediate parent). For the themed flow that's not the value the
// server expects — the ranked-model `slide_order_position` is a flat
// position across ALL admin-block items on the page. We compute it by
// walking every `[data-slide-sortables-target=item]` in DOM order and
// finding the moved item's position. Legacy callers (one container,
// one slide-per-item) get the same answer event.newIndex would have
// given, so the change is back-compatible.
export default class extends Controller {
  static targets = ["item"]

  connect() {
    Sortable.create(this.element, {
      onEnd: this.onEnd.bind(this),
      handle: ".sortable-slide-handle",
      // Shared group across body-inner + every staff-grid `<ul>` so
      // a card can drag within its grid, across grids, and out into
      // the body flow. The next three options are SortableJS's
      // official recipe for nested sortables (see the upstream
      // "nested" demo): `fallbackOnBody: true` lifts the dragged
      // ghost to <body> so its drop-detection isn't tripped by a
      // relatively-positioned ancestor; `swapThreshold: 0.65`
      // tightens the swap zone so the placeholder doesn't bounce
      // into a sibling container before the cursor really crosses
      // its boundary; `forceFallback: true` is required for
      // `fallbackOnBody` to take effect.
      group: "sections",
      animation: 150,
      ghostClass: "opacity-50",
      chosenClass: "ring-2",
      forceFallback: true,
      fallbackOnBody: true,
      swapThreshold: 0.65,
    });
  }

  async onEnd(event) {
    const url = event.item.dataset.slideSortablesPath
    if (!url) return

    const all = Array.from(document.querySelectorAll("[data-slide-sortables-target='item']"))
    const domIndex = all.indexOf(event.item)
    if (domIndex < 0) return
    // Anchor-based reorder transport: send the id of the slide we
    // landed AFTER (or null for "place at top"), and let the server
    // resolve to a ranked-model position. This sidesteps the older
    // off-by-one quirk that a +1 bias on a flat positional index
    // tried to paper over — the +1 worked for nested same-group
    // containers but broke the very-first-slot case (domIndex 0 + 1
    // = position 1, so a "drag to the very top" landed second). The
    // server uses the current order to compute the right position
    // regardless of how SortableJS placed the DOM element.
    const previousItem = domIndex > 0 ? all[domIndex - 1] : null
    const afterSlideId = previousItem ? (previousItem.dataset.slideId || "") : ""

    try {
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": getMetaValue("csrf-token")
        },
        body: JSON.stringify({ slide: { after_slide_id: afterSlideId } })
      })
      if (!res.ok) throw new Error(`reorder failed: ${res.status}`)

      // Refresh via Turbo so the page re-renders with the new order.
      // The themed inner-body re-groups consecutive staff_card slides
      // into a `:staff_grid`, so dropping a body section into the
      // middle of a grid must trigger a re-render to split the grid
      // visually. Legacy callers also benefit — the controller's
      // preview-refresh event isn't watched everywhere.
      if (window.Turbo) {
        window.Turbo.cache.clear()
        window.Turbo.visit(window.location.href, { action: "replace" })
      } else {
        // Legacy fallback (no Turbo on this page): keep the original
        // preview-refresh signal so consumers that listen for it
        // continue to work.
        document.dispatchEvent(new CustomEvent("preview:content-changed", {
          detail: { source: "slide-reorder" }
        }))
      }
    } catch (err) {
      console.error(err)
    }
  }
}
